import { isDefined, isEmpty } from "@utils/var.check";
import { ANNOTATION_TYPE } from "@modules/annotation/service/annotation.constant";
import DOMUtils from "@utils/dom.utils";
import richtextUtils from "@plugins/richtext/utils/richtext.utils";

export default {
  /**
   * Returns the selection content for saving
   * 1. Unwrapping the <sls-highlight> tag in unordered/ordered list, table
   * 2. Extracting data-mathml attribute for WIRIS
   *
   * @param selectedText
   * @returns content
   */
  getSelectionContent: function (selectedText) {
    let content = "";
    const element = document.createElement("div");
    element.innerHTML = selectedText.toHtml();

    if (element.childElementCount === 0) {
      return selectedText.toHtml();
    }

    const children = element.childNodes;
    for (const child in children) {
      // Get valid child nodes, need to check node type to filter valid nodes
      if (isDefined(children[child].nodeType)) {
        // Check nodes with highlights tag first as they have same nodeType with wiris
        if (children[child].nodeName === "SLS-HIGHLIGHT") {
          content += this.recursivelyGetHighlightContent(children[child], 1);
        } else if (children[child].nodeType === Node.TEXT_NODE) {
          if (children[child].nodeValue !== "\n") {
            const text = richtextUtils.wrapTextContentWithDivUsingInnerText(
              children[child].nodeValue,
              {},
            ).innerHTML;
            content += text;
          }
        } else if (children[child].nodeType !== Node.TEXT_NODE) {
          const cloned = children[child].cloneNode(true);
          const wiris = cloned.querySelectorAll(".Wirisformula");
          if (wiris.length > 0) {
            wiris.forEach((element) => {
              const mathml = element.getAttribute("data-mathml");
              const mathmlContainer = document.createElement("div");
              mathmlContainer.innerHTML = mathml;
              const mathmlElement = mathmlContainer.firstElementChild;
              element.parentNode.replaceChild(mathmlElement, element);
            });
            content += cloned.outerHTML;
          } else if (DOMUtils.hasClass(children[child], "Wirisformula")) {
            const wirisFormula = children[child].getAttribute("data-mathml");
            if (!isEmpty(wirisFormula)) {
              content += wirisFormula;
            }
          } else {
            // Unwrap texts inside "sls-highlight" tag
            this.unwrap(children[child], "sls-highlight");
            // Handling on list and table
            const commonAncestorContainerNode = selectedText.getRangeAt(0).commonAncestorContainer;
            if (
              commonAncestorContainerNode.nodeName === "UL" ||
              commonAncestorContainerNode.nodeName === "OL"
            ) {
              return this.getListContent(selectedText, commonAncestorContainerNode);
            }
            if (
              commonAncestorContainerNode.nodeName === "TR" ||
              commonAncestorContainerNode.nodeName === "TBODY"
            ) {
              return this.getTableContent(selectedText, commonAncestorContainerNode);
            }
            content += children[child].outerHTML;
          }
        }
      }
    }
    return content;
  },
  /**
   * Returns the selection content unwrapping <sls-highlight> tag in an unordered/ordered list
   *
   * @param selectedText
   * @param commonAncestorContainerNode
   * @returns content
   */
  getListContent: function (selectedText, commonAncestorContainerNode) {
    const selectedListHtml = document.createElement("div");
    selectedListHtml.innerHTML = selectedText.toHtml();
    const tableContentsLength = selectedListHtml.children.length;
    const listContainer = commonAncestorContainerNode.cloneNode(false);

    // Unwrap texts inside "sls-highlight" tag
    this.unwrap(selectedListHtml, "sls-highlight");

    for (let i = 0; i < tableContentsLength; i++) {
      listContainer.appendChild(selectedListHtml.children[0]);
    }
    return listContainer.outerHTML;
  },
  /**
   * Returns the selection content unwrapping <sls-highlight> tag in a table
   *
   * @param selectedText
   * @param commonAncestorContainerNode
   * @returns content
   */
  getTableContent: function (selectedText, commonAncestorContainerNode) {
    const isCommonAncestorTR = commonAncestorContainerNode.nodeName === "TR";
    const tbodyContainer = isCommonAncestorTR
      ? commonAncestorContainerNode.parentNode.cloneNode(false)
      : commonAncestorContainerNode.cloneNode(false);
    const tableContainer = isCommonAncestorTR
      ? commonAncestorContainerNode.parentNode.parentNode.cloneNode(false)
      : commonAncestorContainerNode.parentNode.cloneNode(false);
    const selectedTableHtml = isCommonAncestorTR
      ? document.createElement("tr")
      : document.createElement("tbody");
    selectedTableHtml.innerHTML = selectedText.toHtml();
    const tableContentsLength = selectedTableHtml.children.length;

    // Unwrap texts inside "sls-highlight" tag
    this.unwrap(selectedTableHtml, "sls-highlight");

    if (isCommonAncestorTR) {
      const trContainer = commonAncestorContainerNode.cloneNode(false);
      for (let i = 0; i < tableContentsLength; i++) {
        trContainer.appendChild(selectedTableHtml.children[0]);
      }
      tbodyContainer.appendChild(trContainer);
    } else {
      for (let i = 0; i < tableContentsLength; i++) {
        tbodyContainer.appendChild(selectedTableHtml.children[0]);
      }
    }
    tableContainer.appendChild(tbodyContainer);
    return tableContainer.outerHTML;
  },
  /**
   * Function to unwrap a particular node given a selector
   *
   * @param node
   * @param selector
   */
  unwrap: function (node, selector) {
    const nodeList = node.querySelectorAll(selector);
    Array.prototype.forEach.call(nodeList, function (item) {
      item.outerHTML = richtextUtils.wrapTextContentWithDivUsingInnerText(
        item.innerText,
        {},
      ).innerHTML;
    });
  },
  /**
   * Returns the highlighted content used in annotation cards
   * Wraps the inner text content with <span> and apply the highlight selector
   *
   * @param data
   * @param selector
   * @returns content
   */
  getHighlightedContent: function (data, selector) {
    let content = "";
    const element = document.createElement("div");
    // Preserve only leading/trailing spaces as non-breaking spaces.
    const normalizedData = data.replace(/^ +| +$/g, function (spaces) {
      return "&nbsp;".repeat(spaces.length);
    });

    element.innerHTML = normalizedData;

    if (element.childElementCount === 0) {
      return this.wrappedContent(normalizedData, selector);
    }

    const children = element.childNodes;
    for (const child in children) {
      if (isDefined(children[child].nodeType)) {
        if (children[child].nodeType === Node.TEXT_NODE) {
          const text = richtextUtils.wrapTextContentWithDivUsingInnerText(
            children[child].nodeValue,
          ).innerHTML;
          content += this.wrappedContent(text, selector);
        } else {
          // Handle for texts inside unordered/ordered list
          if (
            children[child].nodeName === "UL" ||
            children[child].nodeName === "OL" ||
            children[child].nodeName === "TABLE"
          ) {
            content += this.applyHighlightSelectorToContent(children[child], selector);
          } else if (children[child].nodeName === "BR") {
            content += children[child].outerHTML;
          } else if (children[child].nodeName === "math") {
            content += this.wrappedContent(children[child].outerHTML, selector);
          } else {
            content += this.wrapTextContent(children[child], selector);
          }
        }
      }
    }
    return content;
  },
  /**
   * Function to wrap the text content with <span> and specify the highlight selector
   *
   * @param node
   * @param selector
   * @returns content
   */
  wrapTextContent(node, selector) {
    let content = "";

    const algebraContent = node.querySelector(".sls-algebra-kit-content");

    if (algebraContent) {
      const dataSpec = algebraContent.getAttribute("data-spec");

      if (dataSpec) {
        const updatedDataSpec = dataSpec.replace(
          /(<choice-field[\s\S]*?<option\b[^>]*?)\s+label\s*=\s*(['"])[^'"]*\2/gi,
          "$1",
        );

        algebraContent.setAttribute("data-spec", updatedDataSpec);
      }
    }

    if (node.textContent.trim()) {
      if (
        node.children.length > 0 &&
        (node.children[0].nodeName === "TABLE" ||
          node.children[0].nodeName === "UL" ||
          node.children[0].nodeName === "OL")
      ) {
        content = this.applyHighlightSelectorToContent(node.children[0], selector);
      } else {
        node.innerHTML = this.wrappedContent(node.innerHTML, selector);
        content = node.outerHTML;
      }
    } else {
      content = this.wrappedContent(node.innerHTML, selector);
    }
    return content;
  },
  /**
   * Returns the wrapped text with selector inside <span>
   *
   * @param text
   * @param selector
   * @returns {string}
   */
  wrappedContent(text, selector) {
    return `<span class="${selector}">${text}</span>`;
  },
  /**
   * Applies the highlight selector for <span> tags inside unordered/ordered list, table
   *
   * @param node
   * @param selector
   * @returns content
   */
  applyHighlightSelectorToContent: function (node, selector) {
    let nodeList = node.getElementsByTagName("li");
    Array.prototype.forEach.call(
      nodeList,
      function (item) {
        const text = richtextUtils.wrapTextContentWithDivUsingInnerText(
          item.innerHTML,
          {},
        ).innerHTML;
        const textContent = text.trim() ? text.trim() : text;
        item.innerHTML = this.wrappedContent(textContent, selector);
      }.bind(this),
    );

    nodeList = node.getElementsByTagName("span");
    Array.prototype.forEach.call(
      nodeList,
      function (item) {
        const textContent = item.textContent.trim() ? item.textContent.trim() : item.textContent;
        item.outerHTML = this.wrappedContent(textContent, selector);
      }.bind(this),
    );

    return node.outerHTML;
  },
  getAnnotationElement(annotationType, uuid) {
    switch (annotationType) {
      case ANNOTATION_TYPE.TEXT_ANNOTATION:
        return DOMUtils.el(`.page-main sls-highlight[data-annotation-uuid="${uuid}"]`);
      case ANNOTATION_TYPE.IMAGE_ANNOTATION:
        return DOMUtils.el(`.page-main .annotation-points a[data-annotation-uuid="${uuid}"]`);
    }
    return;
  },
  /**
  Remove all SLS-HIGHTLIGHT tag and return the content
  **/
  recursivelyGetHighlightContent: function (node, index) {
    let content = "";
    const children = node.childNodes;
    for (const child in children) {
      if (children[child].nodeName === "SLS-HIGHLIGHT") {
        content += this.recursivelyGetHighlightContent(children[child], ++index);
      } else if (children[child].nodeType === Node.TEXT_NODE) {
        const text = richtextUtils.wrapTextContentWithDivUsingInnerText(
          children[child].nodeValue,
          {},
        ).innerHTML;
        content += text;
      }
    }
    return content;
  },
};
